import { useCallback, useEffect } from 'react';

import { ScreenName } from '@heidi/analytics/vocabulary';
import { ModalId } from '@heidi/schema/ml-scribe';
import type { DictationLogsSpotlightOutcome } from '../../../contracts';
import { getDictationAnalyticsPorts } from '../../../dictation-analytics-ports';

// Module-scoped guard so `screen_viewed` fires at most once per modal id per
// session, regardless of how many times the spotlight re-mounts (sidebar mode
// swaps) or re-becomes visible (user clicks the anchored nav link to
// `/dictation-logs` and later navigates away — the launcher re-enqueues
// because clicking the link doesn't go through `handleClose`, so the modal
// stays un-`seen`). A component-local ref would re-arm on those flips and
// inflate the screen-view count specifically for the converters.
const trackedScreenViews = new Set<ModalId>();

/** @internal — exported for tests to reset between cases. */
export function __resetTrackedScreenViewsForTest() {
  trackedScreenViews.clear();
}

interface UseDictationLogsSpotlightTrackingArgs {
  /** True when the spotlight is actually rendered (flag on, queue open). */
  isVisible: boolean;
  /** The queue's close handler — wrapped here so each dismissal path can attach an outcome. */
  onClose: () => void;
}

/**
 * Owns all analytics for the Dictate-history awareness spotlight:
 *
 *   - Fires `screen_viewed` exactly once per session when the spotlight first
 *     becomes visible.
 *   - Wraps the three explicit-dismissal paths (Got it / Close button / Escape)
 *     so each emits a `dictation_logs_spotlight_dismissed` event with the
 *     correct `outcome` before calling the queue's `onClose`.
 *
 * The launcher's nav-link engagement path (clicking through to
 * `/dictation-logs`) deliberately does **not** flow through any of these
 * handlers — that path doesn't dismiss the modal in the queue's sense, so no
 * dismissal event is fired. Engagement on that path is captured by the
 * `screen_viewed` + lack of subsequent dismissal.
 */
export function useDictationLogsSpotlightTracking({
  isVisible,
  onClose,
}: UseDictationLogsSpotlightTrackingArgs) {
  useEffect(() => {
    if (!isVisible) return;
    if (trackedScreenViews.has(ModalId.DICTATION_LOGS_SPOTLIGHT)) return;
    trackedScreenViews.add(ModalId.DICTATION_LOGS_SPOTLIGHT);
    getDictationAnalyticsPorts().logs.trackScreenViewed({
      screen_name: ScreenName.DictationLogsSpotlight,
    });
  }, [isVisible]);

  const trackDismissed = useCallback((outcome: DictationLogsSpotlightOutcome) => {
    getDictationAnalyticsPorts().logs.trackSpotlightDismissed({ outcome });
  }, []);

  const onGotIt = useCallback(() => {
    trackDismissed('got_it');
    onClose();
  }, [trackDismissed, onClose]);

  const onCloseButton = useCallback(() => {
    trackDismissed('close');
    onClose();
  }, [trackDismissed, onClose]);

  const onEscape = useCallback(() => {
    trackDismissed('escape');
    onClose();
  }, [trackDismissed, onClose]);

  return { onGotIt, onCloseButton, onEscape };
}
